/**
 * Geographic hotspots — outreach volume by state, county, ZIP3.
 *
 * ZIP3 (first 3 digits of ZIP) per HIPAA Safe Harbor; cells with <50 sends
 * suppressed at the SQL layer.
 *
 * Future: replace the grouped table with a US choropleth map (leaflet or
 * react-simple-maps). For now the sortable table makes the hotspots obvious.
 */

import React, { useMemo, useState } from 'react';
import { MapPin } from 'lucide-react';
import { useGeoDistribution } from '@/hooks/ml';

function intensity(value: number, max: number): string {
  if (!max) return 'bg-gray-50';
  const r = value / max;
  if (r >= 0.85) return 'bg-blue-700 text-white';
  if (r >= 0.6) return 'bg-blue-500 text-white';
  if (r >= 0.35) return 'bg-blue-300';
  if (r >= 0.15) return 'bg-blue-200';
  return 'bg-blue-100';
}

export const GeoHotspotsCard: React.FC = () => {
  const { data, isLoading } = useGeoDistribution();
  const [view, setView] = useState<'state' | 'county' | 'zip3'>('state');

  const cells = data?.cells ?? [];

  const rows = useMemo(() => {
    if (!cells.length) return [];
    if (view === 'state') {
      const map = new Map<string, { sends: number; engagements: number; unique_members: number }>();
      for (const c of cells) {
        const k = c.state || 'Unknown';
        const cur = map.get(k) ?? { sends: 0, engagements: 0, unique_members: 0 };
        cur.sends += c.sends;
        cur.engagements += c.engagements;
        cur.unique_members += c.unique_members;
        map.set(k, cur);
      }
      return Array.from(map.entries())
        .map(([key, v]) => ({ key, label: key, ...v }))
        .sort((a, b) => b.sends - a.sends);
    }
    if (view === 'county') {
      return cells
        .map((c) => ({
          key: `${c.state}|${c.county}`,
          label: `${c.county}, ${c.state}`,
          sends: c.sends,
          engagements: c.engagements,
          unique_members: c.unique_members,
        }))
        .sort((a, b) => b.sends - a.sends)
        .slice(0, 50);
    }
    // zip3
    return cells
      .filter((c) => c.zip3 && c.zip3.trim() !== '')
      .map((c) => ({
        key: `${c.state}|${c.zip3}`,
        label: `${c.zip3}xx (${c.state})`,
        sends: c.sends,
        engagements: c.engagements,
        unique_members: c.unique_members,
      }))
      .sort((a, b) => b.sends - a.sends)
      .slice(0, 50);
  }, [cells, view]);

  const maxSends = rows[0]?.sends ?? 1;
  const totalSends = rows.reduce((a, r) => a + r.sends, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-start gap-2 text-xs text-gray-500 flex-1 min-w-0">
          <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
          Where outreach is being concentrated geographically. ZIP shown at
          3-digit precision (HIPAA Safe Harbor). Cells with &lt;50 sends suppressed.
        </div>
        <select
          value={view}
          onChange={(e) => setView(e.target.value as 'state' | 'county' | 'zip3')}
          className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
        >
          <option value="state">By state</option>
          <option value="county">By county (top 50)</option>
          <option value="zip3">By ZIP3 (top 50)</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-500">
          No geographic outreach data above suppression threshold. (Members
          without state/county recorded in legacy data are excluded.)
        </div>
      ) : (
        <div className="overflow-x-auto max-h-[28rem]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-3">Location</th>
                <th className="py-2 pr-3 text-right">Sends</th>
                <th className="py-2 pr-3">Heat</th>
                <th className="py-2 pr-3 text-right">Engaged</th>
                <th className="py-2 pr-3 text-right">Unique members</th>
                <th className="py-2 pr-3 text-right">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const share = totalSends > 0 ? (r.sends / totalSends) * 100 : 0;
                return (
                  <tr key={r.key} className="border-b border-gray-100 last:border-0">
                    <td className="py-1.5 pr-3 font-medium text-gray-900">{r.label}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{r.sends.toLocaleString()}</td>
                    <td className="py-1.5 pr-3">
                      <div
                        className={`h-2 w-32 rounded-sm ${intensity(r.sends, maxSends)}`}
                        title={`${r.sends.toLocaleString()} sends`}
                      />
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{r.engagements.toLocaleString()}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{r.unique_members.toLocaleString()}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{share.toFixed(1)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalSends > 0 && (
        <div className="text-xs text-gray-500">
          Total {view === 'state' ? 'across all states' : view === 'county' ? 'across top 50 counties' : 'across top 50 ZIP3 areas'}:{' '}
          {totalSends.toLocaleString()} sends.
        </div>
      )}
    </div>
  );
};
